import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { Layout } from './components/Layout.tsx';
import { useIsSessionValid } from './lib/auth-store.ts';
import {
  checkForNewBuild,
  hardNavigateIfStale,
  startBuildVersionWatch,
} from './lib/build-version.ts';
import { ConfirmProvider } from './lib/confirm.tsx';
import { queryClient } from './lib/query-client.ts';
import { LoginPage } from './pages/LoginPage.tsx';

// Perf (2026-07-21): route-level code splitting. Previously all ~37 pages were
// statically imported into one bundle → the whole app downloaded on first load
// (over the ~35ms Taiwan↔Japan-East origin link). React.lazy splits each page
// into its own chunk (Vite emits one per dynamic import), so first load pulls
// only the shell + the current route; the rest stream in on navigation.
// Layout + LoginPage stay eager (app shell + the logged-out entry — no Suspense
// flash on login). Named exports need the `.then(m => ({ default: m.X }))` shim.
const AccountingPage = lazy(() =>
  import('./pages/AccountingPage.tsx').then((m) => ({ default: m.AccountingPage })),
);
const AnnouncementDetailPage = lazy(() =>
  import('./pages/AnnouncementDetailPage.tsx').then((m) => ({ default: m.AnnouncementDetailPage })),
);
const AnnouncementsPage = lazy(() =>
  import('./pages/AnnouncementsPage.tsx').then((m) => ({ default: m.AnnouncementsPage })),
);
const ApprovalDetailPage = lazy(() =>
  import('./pages/ApprovalDetailPage.tsx').then((m) => ({ default: m.ApprovalDetailPage })),
);
const ApprovalsPage = lazy(() =>
  import('./pages/ApprovalsPage.tsx').then((m) => ({ default: m.ApprovalsPage })),
);
const AuditLogPage = lazy(() =>
  import('./pages/AuditLogPage.tsx').then((m) => ({ default: m.AuditLogPage })),
);
const EmailDeliveriesPage = lazy(() =>
  import('./pages/EmailDeliveriesPage.tsx').then((m) => ({ default: m.EmailDeliveriesPage })),
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage.tsx').then((m) => ({ default: m.DashboardPage })),
);
const DevToolsPage = lazy(() =>
  import('./pages/DevToolsPage.tsx').then((m) => ({ default: m.DevToolsPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage.tsx').then((m) => ({ default: m.ForgotPasswordPage })),
);
const FundDetailPage = lazy(() =>
  import('./pages/FundDetailPage.tsx').then((m) => ({ default: m.FundDetailPage })),
);
const FundsPage = lazy(() =>
  import('./pages/FundsPage.tsx').then((m) => ({ default: m.FundsPage })),
);
const GpCompaniesPage = lazy(() =>
  import('./pages/GpCompaniesPage.tsx').then((m) => ({ default: m.GpCompaniesPage })),
);
const InvestorDetailPage = lazy(() =>
  import('./pages/InvestorDetailPage.tsx').then((m) => ({ default: m.InvestorDetailPage })),
);
const InvestorsPage = lazy(() =>
  import('./pages/InvestorsPage.tsx').then((m) => ({ default: m.InvestorsPage })),
);
const LeaveAdminPage = lazy(() =>
  import('./pages/LeaveAdminPage.tsx').then((m) => ({ default: m.LeaveAdminPage })),
);
const LeavePage = lazy(() =>
  import('./pages/LeavePage.tsx').then((m) => ({ default: m.LeavePage })),
);
const LedgerPage = lazy(() =>
  import('./pages/LedgerPage.tsx').then((m) => ({ default: m.LedgerPage })),
);
const LpUserDetailPage = lazy(() =>
  import('./pages/LpUserDetailPage.tsx').then((m) => ({ default: m.LpUserDetailPage })),
);
const LpUsersPage = lazy(() =>
  import('./pages/LpUsersPage.tsx').then((m) => ({ default: m.LpUsersPage })),
);
const MagicCallbackPage = lazy(() =>
  import('./pages/MagicCallbackPage.tsx').then((m) => ({ default: m.MagicCallbackPage })),
);
const ApprovalPoliciesPage = lazy(() =>
  import('./pages/ApprovalPoliciesPage.tsx').then((m) => ({ default: m.ApprovalPoliciesPage })),
);
const MasterDataPage = lazy(() =>
  import('./pages/MasterDataPage.tsx').then((m) => ({ default: m.MasterDataPage })),
);
const MyPayslipsPage = lazy(() =>
  import('./pages/MyPayslipsPage.tsx').then((m) => ({ default: m.MyPayslipsPage })),
);
const PartiesPage = lazy(() =>
  import('./pages/PartiesPage.tsx').then((m) => ({ default: m.PartiesPage })),
);
const PartyDetailPage = lazy(() =>
  import('./pages/PartyDetailPage.tsx').then((m) => ({ default: m.PartyDetailPage })),
);
const PayrollAdminPage = lazy(() =>
  import('./pages/PayrollAdminPage.tsx').then((m) => ({ default: m.PayrollAdminPage })),
);
// Pipeline 暫時隱藏 (2026-07-29)，恢復時取消下面的註解。
// const PipelinePage = lazy(() =>
//   import('./pages/PipelinePage.tsx').then((m) => ({ default: m.PipelinePage })),
// );
const PlaceholderPage = lazy(() =>
  import('./pages/PlaceholderPage.tsx').then((m) => ({ default: m.PlaceholderPage })),
);
const ProjectDetailPage = lazy(() =>
  import('./pages/ProjectDetailPage.tsx').then((m) => ({ default: m.ProjectDetailPage })),
);
const ProjectsPage = lazy(() =>
  import('./pages/ProjectsPage.tsx').then((m) => ({ default: m.ProjectsPage })),
);
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage.tsx').then((m) => ({ default: m.ReportsPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage.tsx').then((m) => ({ default: m.ResetPasswordPage })),
);
const RevenuePage = lazy(() =>
  import('./pages/RevenuePage.tsx').then((m) => ({ default: m.RevenuePage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.tsx').then((m) => ({ default: m.SettingsPage })),
);
const SignupPage = lazy(() =>
  import('./pages/SignupPage.tsx').then((m) => ({ default: m.SignupPage })),
);
const SignupRequestsPage = lazy(() =>
  import('./pages/SignupRequestsPage.tsx').then((m) => ({ default: m.SignupRequestsPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/VerifyEmailPage.tsx').then((m) => ({ default: m.VerifyEmailPage })),
);

/** Route guard — anything mounted under <RequireAuth /> redirects to
 *  /login when there's no valid session, instead of rendering the app
 *  shell. Uses replace navigation so the back button doesn't return
 *  to the protected page after logout. */
function RequireAuth() {
  const valid = useIsSessionValid();
  if (!valid) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/**
 * 分頁開很久 → 伺服器已經換版 → 一換頁就出錯（chunk 404，或舊前端對不上新 API）。
 * 見 lib/build-version.ts。 這裡負責兩件事：
 *   1. 掛上版本監看（回到分頁時 + 每 5 分鐘對一次）
 *   2. 換頁時如果已知換版了，改用整頁導航 —— 直接落在新版，不會先撞到錯誤
 *
 * 放在 Router 內、路由外層，才拿得到 location 又不會被路由切換卸載。
 * 首次進站不導航（第一個 location 就是使用者剛載入的那一頁）。
 */
function BuildVersionGate() {
  const location = useLocation();
  const first = useRef(true);

  useEffect(() => {
    void checkForNewBuild();
    return startBuildVersionWatch();
  }, []);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    hardNavigateIfStale(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}

// Suspense fallback shown while a lazy route chunk streams in (only on the
// first visit to each page — chunks are then cached). Kept minimal to avoid
// pulling anything into the eager bundle.
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <svg
          className="h-7 w-7 animate-spin text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="text-sm text-slate-400">載入中…</span>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          {/* 頁內確認對話框的 Provider —— 取代原生 confirm()，見 lib/confirm.tsx。
              放在 ErrorBoundary 內、Router 內：對話框要能用 router 的 context，
              而它自己出錯也該被 ErrorBoundary 接住。 */}
          <ConfirmProvider>
            <BuildVersionGate />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* /login is the only route that renders without the app shell. */}
                <Route path="/login" element={<LoginPage />} />
                {/* 2026-06-15: /login/callback kept as harmless dead route — Entra
              SSO removed; no inbound traffic expected. Will be deleted later. */}
                <Route path="/login/callback" element={<LoginPage />} />
                {/* 2026-06-15 V158: 自己的 password reset 流程 */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* Self-signup + magic-link verification + callback (all public). */}
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                <Route path="/auth/magic-callback" element={<MagicCallbackPage />} />

                {/* Everything else lives behind the auth gate. */}
                <Route element={<RequireAuth />}>
                  <Route element={<Layout />}>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    {/* Pipeline 暫時隱藏 (2026-07-29) */}
                    {/* <Route path="/pipeline" element={<PipelinePage />} /> */}
                    {/* 財務模組 — 2026-07-16 收斂成 4 個容器頁 (原本 7 個 nav 項):
                  報表 / 會計 / 帳款 / 收入。 舊路徑一律 redirect 到新容器的對應
                  分頁 (?tab=), 舊書籤不會落空。 */}
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/accounting" element={<AccountingPage />} />
                    <Route path="/payables" element={<LedgerPage />} />
                    <Route path="/revenue" element={<RevenuePage />} />
                    <Route path="/budget" element={<Navigate to="/reports?tab=budget" replace />} />
                    <Route
                      path="/receivables"
                      element={<Navigate to="/payables?tab=ar" replace />}
                    />
                    <Route
                      path="/contracts"
                      element={<Navigate to="/payables?tab=contracts" replace />}
                    />
                    <Route
                      path="/bank-rec"
                      element={<Navigate to="/accounting?tab=bank" replace />}
                    />
                    {/* 2026-07-16: GP 拉回獨立頁 (user: GP 是 ERP 主體, 設定要拉上一級) */}
                    {/* V339 改名: GP 公司 → 記帳主體 (同時管 GP 公司 + 基金法人公司)。
                    舊路徑 redirect, 書籤/既有連結不落空。 */}
                    <Route path="/ledger-entities" element={<GpCompaniesPage />} />
                    <Route
                      path="/gp-companies"
                      element={<Navigate to="/ledger-entities" replace />}
                    />
                    {/* 2026-07-05: 管理費收入頁併入「收入」容器 (管理費 + carry) — 舊路徑 redirect */}
                    <Route path="/fee-revenue" element={<Navigate to="/revenue" replace />} />
                    <Route path="/parties" element={<PartiesPage />} />
                    <Route path="/parties/new" element={<PartiesPage />} />
                    <Route path="/parties/:id" element={<PartyDetailPage />} />
                    <Route path="/funds" element={<FundsPage />} />
                    <Route path="/funds/new" element={<FundsPage />} />
                    <Route path="/funds/:id" element={<FundDetailPage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/new" element={<ProjectsPage />} />
                    <Route path="/projects/:id" element={<ProjectDetailPage />} />
                    <Route path="/investors" element={<InvestorsPage />} />
                    <Route path="/investors/new" element={<InvestorsPage />} />
                    <Route path="/investors/:id" element={<InvestorDetailPage />} />
                    <Route path="/lp-users" element={<LpUsersPage />} />
                    <Route path="/lp-users/:id" element={<LpUserDetailPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/signup-requests" element={<SignupRequestsPage />} />
                    <Route path="/approval-policies" element={<ApprovalPoliciesPage />} />
                    <Route path="/master-data" element={<MasterDataPage />} />
                    <Route path="/announcements" element={<AnnouncementsPage />} />
                    <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="/email-deliveries" element={<EmailDeliveriesPage />} />
                    {/* 2026-07-30 使用者:「workflow 跟簽核路線 能不能整合成一個」
                      → 流程設定統一到 /approval-policies。舊書籤不落空。 */}
                    <Route
                      path="/workflows"
                      element={<Navigate to="/approval-policies" replace />}
                    />
                    {/* 2026-07-20: 「我的待辦」併進簽核中心 → 舊路徑 redirect (書籤/email 連結不落空) */}
                    <Route
                      path="/my-inbox"
                      element={<Navigate to="/approvals?tab=inbox" replace />}
                    />
                    <Route path="/approvals" element={<ApprovalsPage />} />
                    <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
                    <Route path="/leave" element={<LeavePage />} />
                    <Route path="/leave-admin" element={<LeaveAdminPage />} />
                    <Route path="/my-payslips" element={<MyPayslipsPage />} />
                    <Route path="/payroll" element={<PayrollAdminPage />} />
                    <Route path="/dev-tools" element={<DevToolsPage />} />
                    <Route path="/commitments" element={<Navigate to="/funds" replace />} />
                    <Route
                      path="*"
                      element={<PlaceholderPage title="404" upcoming="這個路徑還沒接。" />}
                    />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </ConfirmProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
